import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PlanLimitTooltip from '@/components/PlanLimitTooltip';
import {
  CheckCircle, Edit, RefreshCw, XCircle, Archive,
  Eye, MessageSquare
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

export function PositionCard({
  position,
  schoolTOCs,
  onEdit,
  onRemind,
  onCancel,
  onArchive,
  onViewDeclined,
  onMessageSub,
  messagingDisabled = false,
  messagingDisabledMessage
}) {
  const dates = position.dates || [];
  const isMultiDay = dates.length > 1;
  
  const dateDisplay = useMemo(() => {
    if (dates.length === 0) {
      const startDate = position.start_date || position.date;
      return format(parseISO(startDate), 'EEEE, MMMM d, yyyy');
    } else if (dates.length === 1) {
      return format(parseISO(dates[0]), 'EEEE, MMMM d, yyyy');
    }
    return dates.map(d => format(parseISO(d), 'MMM d')).join(', ');
  }, [dates, position.start_date, position.date]);

  const responses = position.responses || {};
  const acceptedCount = Object.values(responses).filter(r => r === 'accept').length;
  const declinedCount = Object.values(responses).filter(r => r === 'decline' || r === 'declined_unavailable').length;
  const pendingCount = Object.values(responses).filter(r => r === 'pending').length;
  
  const offeredTocs = useMemo(() => {
    if (position.status !== 'open' || !position.target_tocs?.length) return [];
    return schoolTOCs
      .filter(toc => position.target_tocs.includes(toc.user_id))
      .sort((a, b) => (a.last_name || a.name?.split(' ').pop() || '').localeCompare(b.last_name || b.name?.split(' ').pop() || ''));
  }, [schoolTOCs, position.target_tocs, position.status]);

  return (
    <Card className="border-slate-200 position-card">
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <Badge className={`status-${position.status}`}>
                {position.status.charAt(0).toUpperCase() + position.status.slice(1)}
              </Badge>
              {isMultiDay && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {dates.length} days
                </Badge>
              )}
              <span className="text-sm text-slate-500">
                {dateDisplay}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1">
              {position.grade_level?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'General'}
            </h3>
            <p className="text-sm text-slate-600">
              {position.start_time} - {position.end_time}
            </p>
            {position.description && (
              <p className="text-sm text-slate-500 mt-2">{position.description}</p>
            )}
            
            {/* Filled by info + message button */}
            {position.accepted_by_name && (
              <div className="mt-2">
                <p className="text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Filled by {position.accepted_by_name}
                </p>
                <PlanLimitTooltip
                  variant="outline"
                  size="sm"
                  className="mt-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMessageSub(position.school_id, position.accepted_by, position.accepted_by_name);
                  }}
                  data-testid={`message-filled-sub-${position.position_id}`}
                  disabled={messagingDisabled}
                  message={messagingDisabledMessage}
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  Send message
                </PlanLimitTooltip>
              </div>
            )}
            
            {/* Offered subs for open positions */}
            {position.status === 'open' && position.target_tocs?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Offered to {position.target_tocs.length} sub{position.target_tocs.length !== 1 ? 's' : ''}:</p>
                <div className="flex flex-wrap gap-2">
                  {offeredTocs.map(toc => {
                      const response = responses[toc.user_id];
                      const isDeclined = response === 'decline' || response === 'declined_unavailable';
                      const isAccepted = response === 'accept';
                      const badgeClass = (() => {
                        if (isDeclined) return 'bg-rose-50 text-rose-600 border-rose-200 line-through';
                        if (isAccepted) return 'bg-emerald-50 text-emerald-600 border-emerald-200';
                        return 'bg-slate-50 text-slate-600 border-slate-200';
                      })();
                      return (
                        <Badge 
                          key={toc.user_id}
                          variant="outline"
                          className={`text-xs ${badgeClass}`}
                        >
                          {toc.first_name || toc.name?.split(' ')[0]} {toc.last_name || toc.name?.split(' ').pop()}
                          {isDeclined && ' (declined)'}
                          {isAccepted && ' \u2713'}
                        </Badge>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col items-end gap-2">
            {/* View Status */}
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Eye className="h-3 w-3" />
              {position.viewed_by?.length || 0} of {position.target_tocs?.length || 0} viewed
            </div>
            
            {/* Response Summary */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-emerald-600">{acceptedCount} accepted</span>
              <span className="text-slate-300">|</span>
              <button 
                className="text-rose-600 hover:underline cursor-pointer"
                onClick={() => onViewDeclined(position)}
                data-testid={`view-declined-${position.position_id}`}
              >
                {declinedCount} declined
              </button>
              <span className="text-slate-300">|</span>
              <span className="text-slate-500">{pendingCount} pending</span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onEdit(position)}
                data-testid={`edit-position-${position.position_id}`}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              
              {position.status === 'open' && pendingCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => onRemind(position.position_id)}
                  data-testid={`send-reminders-${position.position_id}`}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Remind
                </Button>
              )}
              
              {position.status === 'open' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => onCancel(position.position_id)}
                  data-testid={`cancel-position-${position.position_id}`}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              )}
              
              {(position.status === 'filled' || position.status === 'cancelled') && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-slate-500 border-slate-200 hover:bg-slate-50"
                  onClick={() => onArchive(position.position_id)}
                  data-testid={`archive-position-${position.position_id}`}
                >
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
